/**
 * Public site header: logo + Login and Get Started actions.
 *
 * Everything is driven by header_settings. Sits transparently over the hero and
 * (optionally) turns solid on scroll. Used by both the live landing page and the
 * admin live preview, so they can never drift apart.
 */

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { HEADER_DEFAULTS, trackCtaClick } from '@/utils/heroStyles';

const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-black/40';

const GetStartedLink = ({ header, mobile, onNavigate }) => (
  <Link
    to={header.get_started_url}
    {...(header.get_started_new_tab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
    onClick={() => {
      trackCtaClick('header_get_started');
      onNavigate?.();
    }}
    className={`inline-flex items-center justify-center px-5 py-2.5 text-sm font-semibold shadow-sm
      transition-all duration-200 hover:brightness-110 hover:shadow-md active:scale-[0.97] ${FOCUS_RING}
      ${mobile ? 'w-full' : ''}`}
    style={{
      backgroundColor: header.get_started_bg_color,
      color: header.get_started_text_color,
      borderRadius: `${header.get_started_radius}px`,
    }}
    data-testid={mobile ? 'header-get-started-mobile' : 'header-get-started'}
  >
    {header.get_started_label}
  </Link>
);

export const SiteHeader = ({
  branding,
  header: headerProp,
  overHero = true,
  mobile = false,
  preview = false,
}) => {
  const header = { ...HEADER_DEFAULTS, ...(headerProp || {}) };
  const [scrolled, setScrolled] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    if (preview || header.style_over_hero !== 'solid_on_scroll') return undefined;
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [preview, header.style_over_hero]);

  const solid = !overHero || header.style_over_hero === 'solid' || (header.style_over_hero === 'solid_on_scroll' && scrolled);
  const appName = branding?.app_name || 'MemberHub';

  const shellClass = solid
    ? 'bg-card/95 backdrop-blur-md border-b border-border shadow-sm'
    : 'bg-gradient-to-b from-black/55 to-transparent border-b border-transparent';
  const nameClass = solid ? 'text-foreground' : 'text-white drop-shadow-[0_1px_8px_rgba(0,0,0,0.75)]';

  return (
    <header
      className={`${preview ? 'relative' : 'fixed inset-x-0 top-0'} z-40 transition-colors duration-300 ${shellClass}`}
      data-testid="site-header"
    >
      <div className={`${preview ? 'px-4' : 'container mx-auto px-6'} flex items-center justify-between gap-4 py-4`}>
        <Link
          to="/"
          className={`flex items-center gap-3 ${FOCUS_RING} rounded-md`}
          aria-label={`${appName} home`}
          data-testid="header-logo-link"
        >
          {branding?.logo_url && branding?.use_logo_in_header ? (
            <img src={branding.logo_url} alt={appName} className="h-9 w-auto" data-testid="landing-logo" />
          ) : (
            <span className={`text-xl sm:text-2xl font-heading font-bold ${nameClass}`} data-testid="landing-app-name">
              {appName}
            </span>
          )}
        </Link>

        {/* Desktop actions */}
        <nav className={`items-center gap-3 ${mobile ? 'hidden' : 'hidden md:flex'}`} aria-label="Primary">
          {header.login_show && (
            <Link
              to={header.login_url}
              className={`inline-flex items-center justify-center rounded-full px-4 py-2 text-sm font-medium
                transition-colors duration-200 ${FOCUS_RING}
                ${solid
                  ? 'text-foreground hover:bg-muted border border-border'
                  : 'text-white border border-white/70 hover:bg-white/15'}`}
              data-testid="login-nav-button"
            >
              {header.login_label}
            </Link>
          )}
          {header.get_started_show && <GetStartedLink header={header} />}
        </nav>

        {/* Mobile: primary action stays one tap away, rest in the drawer */}
        <div className={`items-center gap-2 ${mobile ? 'flex' : 'flex md:hidden'}`}>
          {header.get_started_show && (
            <Link
              to={header.get_started_url}
              {...(header.get_started_new_tab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
              onClick={() => trackCtaClick('header_get_started')}
              className={`inline-flex items-center justify-center px-3.5 py-2 text-xs font-semibold shadow-sm
                transition-all duration-200 active:scale-[0.97] ${FOCUS_RING}`}
              style={{
                backgroundColor: header.get_started_bg_color,
                color: header.get_started_text_color,
                borderRadius: `${header.get_started_radius}px`,
              }}
              data-testid="header-get-started-compact"
            >
              {header.get_started_label}
            </Link>
          )}
          <button
            type="button"
            onClick={() => setDrawerOpen((v) => !v)}
            aria-expanded={drawerOpen}
            aria-label={drawerOpen ? 'Close menu' : 'Open menu'}
            className={`inline-flex h-9 w-9 items-center justify-center rounded-md transition-colors ${FOCUS_RING}
              ${solid ? 'text-foreground hover:bg-muted' : 'text-white hover:bg-white/15'}`}
            data-testid="header-menu-toggle"
          >
            {drawerOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      {drawerOpen && (
        <div
          className="border-t border-border bg-card px-6 py-4 space-y-3 shadow-lg"
          data-testid="header-mobile-drawer"
        >
          {header.get_started_show && (
            <GetStartedLink header={header} mobile onNavigate={() => setDrawerOpen(false)} />
          )}
          {header.login_show && (
            <Link to={header.login_url} onClick={() => setDrawerOpen(false)} className="block">
              <Button variant="outline" className="w-full" data-testid="login-nav-button-mobile">
                {header.login_label}
              </Button>
            </Link>
          )}
        </div>
      )}
    </header>
  );
};

export default SiteHeader;
